(() => {
  const getHeaderOffset = () => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-h').trim();
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : 80;
  };

  const prefersReducedMotion = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;

  const scrollToWithOffset = (element, extraOffset = 16) => {
    if (!element) return;

    const headerOffset = getHeaderOffset();
    const targetTop = element.getBoundingClientRect().top + window.pageYOffset - headerOffset - extraOffset;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  const initBackToTop = () => {
    const button = document.getElementById('back-to-top');
    if (!button) return;

    let visible = false;
    let scrolling = false;

    const checkScroll = () => {
      if (scrolling) return;
      scrolling = true;

      requestAnimationFrame(() => {
        const shouldShow = window.pageYOffset > 400;

        if (shouldShow && !visible) {
          visible = true;
          button.classList.add('visible');
        } else if (!shouldShow && visible) {
          visible = false;
          button.classList.remove('visible');
        }

        scrolling = false;
      });
    };

    let throttleTimeout;
    window.addEventListener(
      'scroll',
      () => {
        if (throttleTimeout) return;
        throttleTimeout = setTimeout(() => {
          checkScroll();
          throttleTimeout = null;
        }, 16);
      },
      { passive: true },
    );

    button.addEventListener('click', (event) => {
      event.preventDefault();

      button.style.transform = 'translateY(-2px) scale(0.95)';
      window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      setTimeout(() => {
        button.style.transform = '';
      }, 150);
    });

    checkScroll();
  };

  const initDataSrcLazyImages = () => {
    if (!('IntersectionObserver' in window)) return;

    const candidates = document.querySelectorAll('img[data-src]');
    if (!candidates.length) return;

    const imageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const img = entry.target;
          if (!img.dataset.src) {
            imageObserver.unobserve(img);
            return;
          }

          img.src = img.dataset.src;
          if (img.dataset.srcset) img.srcset = img.dataset.srcset;
          img.classList.add('loaded');
          imageObserver.unobserve(img);
        });
      },
      { rootMargin: '50px', threshold: 0.01 },
    );

    candidates.forEach((img) => imageObserver.observe(img));
  };

  const ensureSessionIdCookie = () => {
    if (document.cookie.match(/(?:^|; )session_id=/)) return;

    const id =
      globalThis.crypto?.randomUUID?.() ||
      Date.now().toString(36) + Math.random().toString(36).substr(2);
    document.cookie = `session_id=${id};path=/;max-age=31536000;SameSite=Lax;Secure`;
  };

  const detectFontsLoaded = () => {
    if (!('fonts' in document)) return;
    document.fonts.ready
      .then(() => {
        document.documentElement.classList.add('fonts-loaded');
      })
      .catch(() => {
        if (window.Shopify && window.Shopify.designMode) console.warn('Error loading fonts');
      });
  };

  const hardenBlankTargetLinks = () => {
    const links = document.querySelectorAll('a[target="_blank"]');
    if (!links.length) return;

    links.forEach((link) => {
      const existing = (link.getAttribute('rel') || '')
        .split(/\s+/)
        .map((token) => token.trim())
        .filter(Boolean);

      const relSet = new Set(existing.map((token) => token.toLowerCase()));
      relSet.add('noopener');
      relSet.add('noreferrer');

      const merged = Array.from(relSet);
      if (!merged.length) return;
      link.setAttribute('rel', merged.join(' '));
    });
  };

  const initFaqUx = () => {
    const faqList = document.getElementById('faq-list');
    if (!faqList) return;
    if (faqList.dataset.faqUxInit === '1') return;
    faqList.dataset.faqUxInit = '1';

    const faqItems = Array.from(document.querySelectorAll('.faq-item'));
    const categoryButtons = Array.from(document.querySelectorAll('.cat-card'));
    const tagButtons = Array.from(document.querySelectorAll('.tag-btn'));
    const searchInput = document.getElementById('faq-search');
    const resultsCount = document.getElementById('results-count');

    if (!faqItems.length || !categoryButtons.length) return;

    if (searchInput && !searchInput.hasAttribute('aria-label')) {
      const placeholder = searchInput.getAttribute('placeholder')?.trim();
      searchInput.setAttribute('aria-label', placeholder || 'Buscar en FAQ');
    }

    if (resultsCount) {
      resultsCount.setAttribute('role', 'status');
      resultsCount.setAttribute('aria-live', 'polite');
      resultsCount.setAttribute('aria-atomic', 'true');
    }

    const getFirstVisibleFaqItem = () =>
      faqItems.find((item) => item.style.display !== 'none') || null;

    const openFaqItem = (item) => {
      if (!item) return;

      faqItems.forEach((other) => {
        if (other !== item) other.classList.remove('active');
      });
      item.classList.add('active');
    };

    const scrollToResults = () => {
      const threshold = getHeaderOffset() + 24;
      const rect = faqList.getBoundingClientRect();

      if (rect.top <= threshold && rect.bottom >= threshold) return;
      scrollToWithOffset(faqList, 24);

      const first = getFirstVisibleFaqItem();
      const firstQuestion = first?.querySelector('.faq-question');
      firstQuestion?.focus({ preventScroll: true });
    };

    const updateCategoryAriaPressed = () => {
      categoryButtons.forEach((button) => {
        button.setAttribute('aria-pressed', button.classList.contains('active') ? 'true' : 'false');
      });
    };

    updateCategoryAriaPressed();

    categoryButtons.forEach((button) => {
      button.addEventListener('click', () => {
        document.documentElement.classList.add('faq-interacted');
        requestAnimationFrame(() => {
          updateCategoryAriaPressed();
          scrollToResults();
        });
      });
    });

    tagButtons.forEach((button) => {
      button.addEventListener('click', () => {
        document.documentElement.classList.add('faq-interacted');

        setTimeout(() => {
          const first = getFirstVisibleFaqItem();
          if (first) openFaqItem(first);
          scrollToWithOffset(first || faqList, 24);
        }, 350);
      });
    });

    if (searchInput) {
      searchInput.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        document.documentElement.classList.add('faq-interacted');
        searchInput.dispatchEvent(new Event('input'));

        setTimeout(() => {
          const first = getFirstVisibleFaqItem();
          if (first) openFaqItem(first);
          scrollToWithOffset(first || faqList, 24);
        }, 350);
      });
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    initBackToTop();
    initDataSrcLazyImages();
    ensureSessionIdCookie();
    hardenBlankTargetLinks();
    initFaqUx();
  });

  detectFontsLoaded();
})();
